import React, {forwardRef, useMemo, useReducer} from 'react';
import {FormGroup} from '@blueprintjs/core';
import {useSelector} from 'react-redux';

import {blockResultSelector} from '../../../store/reducers/blockResultReducer';
import {ContentCard, ContentToolbar, NoIntegration, ApiResult, Attachments} from '../components';
import {
  useCreateBlock,
  useExecuteRequest,
  useGetIntegrations,
  useInitBlock,
  useLoadResult,
  useUpdateBlockContentJson,
} from '../hooks';
import {BaseBlock, ContentActions, ContentOptions} from '../types';
import {defaultContentActions, defaultContentOptions} from '../constants';
import {SimpleSelect} from '../../select';
import {contentReducer, setContentFormKeyValue, setPreview, unsetPreview} from '../store';
import {integrationToSelectOption, shouldLoadResult} from '../utils';
import {createDefaultContent} from './utils';
import Textarea from './Textarea';
import CollapseCard from './CollapseCard';
import {DEFAULT_MODEL, MODEL_OPTIONS} from './constants';
import {initialContentState} from './store';

import './OpenAI.scss';
import {IState} from "../../../store/reducers";

interface OpenAIProps {
  content: BaseBlock;
  options?: ContentOptions;
  actions?: ContentActions;
}

export const OpenAI = forwardRef(({
  content,
  options = defaultContentOptions,
  actions = defaultContentActions,
}: OpenAIProps, ref: any) => {
  options = useMemo((): ContentOptions => ({...defaultContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const [contentState, contentDispatch] = useReducer(contentReducer, initialContentState);

  const uid = content.uid;
  const openCollapse = useMemo(() =>
    contentState.form.model !== DEFAULT_MODEL || !!contentState.form.system_message, [contentState.init]);

  const blocks = useSelector((state: IState) => state.block.blocks);
  const result = useSelector(blockResultSelector(uid as string, content.result_id as string));
  const resultId: string | null = content.result_id || result?.key || null;

  const {integrations, hasIntegrations} = useGetIntegrations(content.type);
  const {executing, executeRequest} = useExecuteRequest(uid);
  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);
  useLoadResult(uid, resultId,
    options.loadResult !== undefined ? options.loadResult : shouldLoadResult(content.result_id, result)
  );

  useCreateBlock(content, createDefaultContent());
  useInitBlock(uid, content, contentDispatch);

  const onExecute = () => {
    const block: any = {
      content_json: {
        integration_id: contentState.form.integration_id,
        model: contentState.form.model,
        system_message: contentState.form.system_message || '',
        prompt: contentState.form.prompt || '',
        attachments: contentState.form.attachments || [],
      }
    };
    executeRequest(block);
  };

  const onAttachmentChange = (attachments: string[]) => {
    contentDispatch(setContentFormKeyValue('attachments', attachments));
    updateBlockContentJson('attachments', attachments);
  };

  const onPreview = (data: any) => {
    if (!data) {
      contentDispatch(unsetPreview());
    } else {
      try {
        const jsonData = JSON.parse(data);

        contentDispatch(setPreview({
          system_message: jsonData.system_message,
          prompt: jsonData.prompt,
        }));
      } catch (e) {
        contentDispatch(setPreview({
          system_message: '',
          prompt: e.message,
        }));
      }
    }
  };

  const onIntegrationIdChange = (value: any) => {
    contentDispatch(setContentFormKeyValue('integration_id', value));
    updateBlockContentJson('integration_id', value);
  }

  const onModelChange = (value: any) => {
    updateBlockContentJson('model', value.value);
    contentDispatch(setContentFormKeyValue('model', value.value));
  }

  const onTextAreaChange = (key: string) => (event: any) => {
    const value = event.target.value;

    updateBlockContentJson(key, value);
    contentDispatch(setContentFormKeyValue(key, value));
  };

  const shouldShowAttachments = (model: string): boolean => {
    // these models accept attachments as image url input for open ai api
    const tableauBlocks = blocks?.find((block) =>
      block.uid !== uid
      && block.type === 'tableau'
      && block.content_json.action === 'view_image'
    );
    const allowedModels = ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo'];
    return allowedModels.includes(model) && !!tableauBlocks;
  };

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={contentState.init ? uid : null}
      disabled={!hasIntegrations || options.readOnly}
      noContentElement={hasIntegrations ? null : <NoIntegration
        contentId={uid as string}
        type={content.type}
        onDelete={actions.onDelete}
        showDelete={options.showDelete}
      />}
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
      showFooter={options.showFooter}
    >
      <div className="content__open-ai">
        <ContentToolbar
          show={options?.showToolbar}
          contentId={uid as string}
          executing={executing}
          onExecute={options.showRun ? onExecute : null}
          updating={updating}
          selected={contentState.form.integration_id}
          onSelect={onIntegrationIdChange}
          options={integrations.map(integrationToSelectOption)}
          onPreview={onPreview}
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showSave={false}
          disabled={options.readOnly || false}
          showPreview={options.showPreview}
        />
        <div className="content-card__content__form">
          <div className="content__open-ai__form">
            <CollapseCard
              open={openCollapse}
            >
              <FormGroup label="Model" inline={true} className="select-model">
                <SimpleSelect
                  selected={contentState.form.model}
                  disabled={contentState.isPreview || options.readOnly || false}
                  onChange={onModelChange}
                  options={MODEL_OPTIONS}
                  buttonProps={{
                    minimal: true,
                    small: true,
                    outlined: true,
                  }}
                  popoverProps={{
                    usePortal: true
                  }}
                />
              </FormGroup>
              {
                options.showAttachments && shouldShowAttachments(contentState.form.model) && (
                  <Attachments
                    value={contentState.form.attachments}
                    onChange={onAttachmentChange}
                    disabled={contentState.isPreview || options.readOnly}
                    inline={true}
                    className="select-attachments"
                  />
                )
              }
              <Textarea
                label="System-Message"
                value={contentState.form.system_message}
                placeholder="You are a world-class data analyst"
                onChange={onTextAreaChange('system_message')}
                disabled={contentState.isPreview || options.readOnly}
              />
            </CollapseCard>
            <Textarea
              label="Prompt"
              value={contentState.form.prompt}
              placeholder="Describe the main patterns in the given dataset: {{ query_1.data }}"
              onChange={onTextAreaChange('prompt')}
              disabled={contentState.isPreview || options.readOnly}
            />
          </div>
          <ApiResult
            result={result}
            failedTitle="OpenAI run failed"
          />
        </div>
      </div>
    </ContentCard>
  );
});
